import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Inbox, RefreshCw, Loader2, Copy, Send, CheckCircle2, ExternalLink, Dog, CalendarDays, CreditCard,
  MessageCircleReply, ThumbsUp, Save, XCircle,
} from "lucide-react";
import { OpsSectionHead } from "@/components/OpsSectionHead";
import { RequestedMessageConfig } from "@/components/RequestedMessageConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ops/requested`;
const MAX = 320;
const chars = (t) => [...(t || "")].length;
const fmt = (iso) => new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", weekday: "short", month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" });
const ago = (iso) => {
  if (!iso) return "never";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 1 ? "just now" : m < 60 ? `${m}m ago` : m < 1440 ? `${Math.round(m / 60)}h ago` : `${Math.round(m / 1440)}d ago`;
};

const StatusBadge = ({ it }) => {
  if (it.reply_yes) return <Badge className="bg-emerald-600 text-white" data-testid={`req-status-${it.key}`}><ThumbsUp className="w-3 h-3 mr-1" />Customer replied YES — confirm in Gingr</Badge>;
  if (it.reply_at) return <Badge className="bg-rose-100 text-rose-700 border-rose-200" data-testid={`req-status-${it.key}`}><MessageCircleReply className="w-3 h-3 mr-1" />Customer replied</Badge>;
  if (it.send_status === "sending") return <Badge className="bg-cyan-100 text-cyan-800" data-testid={`req-status-${it.key}`}><Loader2 className="w-3 h-3 mr-1 animate-spin" />Sending via Gingr…</Badge>;
  if (it.send_status === "failed") return <Badge className="bg-rose-100 text-rose-700" data-testid={`req-status-${it.key}`}><XCircle className="w-3 h-3 mr-1" />Send failed</Badge>;
  if (it.texted_at) return <Badge className="bg-slate-100 text-slate-700" data-testid={`req-status-${it.key}`}><CheckCircle2 className="w-3 h-3 mr-1" />Texted {ago(it.texted_at)}{it.sent_via === "gingr" ? " via Gingr" : ""}</Badge>;
  return <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid={`req-status-${it.key}`}>Needs reply</Badge>;
};

const RequestCard = ({ it, gingrUrl, onChanged }) => {
  const [text, setText] = useState(it.draft || "");
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { setText(it.draft || ""); }, [it.draft]);
  const n = chars(text);
  const over = n > MAX;
  const dirty = it.draft && text.trim() !== it.draft.trim();
  const call = async (k, fn, ok) => {
    setBusy(k);
    try { await fn(); if (ok) toast.success(ok); await onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); } finally { setBusy(""); }
  };
  const tid = it.reservation_ids[0];

  return (
    <div className={`rounded-xl border bg-white p-4 shadow-sm ${it.reply_yes ? "border-emerald-300 ring-1 ring-emerald-200" : "border-gray-200"}`} data-testid={`req-card-${tid}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-bold text-gray-900" data-testid={`req-owner-${tid}`}>{it.owner.first_name} {it.owner.last_name}</span>
            <span className="inline-flex items-center gap-1 text-sm text-amber-700 font-semibold"><Dog className="w-4 h-4" />{it.dog_names.join(" & ") || "—"}</span>
            {it.breeds.filter(Boolean).length > 0 && <span className="text-xs text-gray-400">{it.breeds.filter(Boolean).join(", ")}</span>}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-gray-600">
            <span className="font-semibold text-gray-800" data-testid={`req-type-${tid}`}>{it.type}</span>
            <span className="inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{fmt(it.start)} → {fmt(it.end)}</span>
            {it.deposit > 0 && <span className="inline-flex items-center gap-1"><CreditCard className="w-3.5 h-3.5" />Deposit ${it.deposit.toFixed(2)}{it.deposit_paid > 0 ? ` (paid $${it.deposit_paid.toFixed(2)})` : " · unpaid"}</span>}
            <span className="text-gray-400">Requested {ago(it.created)}</span>
          </div>
          {it.notes && <p className="mt-1 text-xs text-gray-500">Note: {it.notes}</p>}
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <StatusBadge it={it} />
          {gingrUrl && it.owner.id && (
            <a href={`${gingrUrl}/owners/view/id/${it.owner.id}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:underline" data-testid={`req-open-gingr-${tid}`}>
              <ExternalLink className="w-3 h-3" />Open in Gingr
            </a>
          )}
        </div>
      </div>

      {it.reply_text && (
        <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-sm text-emerald-900" data-testid={`req-reply-${tid}`}>
          <MessageCircleReply className="inline w-4 h-4 mr-1" />“{it.reply_text}” <span className="text-emerald-700/70 text-xs">· {ago(it.reply_at)}</span>
        </div>
      )}

      {it.is_boarding ? (
        <div className="mt-3 space-y-2">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} className={over ? "border-rose-400 focus-visible:ring-rose-400" : ""} data-testid={`req-draft-${tid}`} />
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-xs font-semibold ${over ? "text-rose-600" : n > 300 ? "text-amber-600" : "text-gray-400"}`} data-testid={`req-count-${tid}`}>{n}/{MAX}{over ? " — too long" : ""}</span>
            {it.draft_edited && <span className="text-[11px] text-gray-400">edited</span>}
            {it.template_overflow && <span className="text-[11px] text-amber-600" data-testid={`req-tpl-overflow-${tid}`}>Template too long for this stay — standard text used</span>}
            <div className="ml-auto flex flex-wrap gap-2">
              <Button size="sm" variant="outline" disabled={!dirty || over || !!busy} data-testid={`req-save-${tid}`}
                onClick={() => call("save", () => axios.put(`${API}/draft`, { key: it.key, text: text.trim() }), "Draft saved")}>
                <Save className="w-3.5 h-3.5 mr-1" />Save
              </Button>
              <Button size="sm" variant="outline" disabled={over || !text.trim()} data-testid={`req-copy-${tid}`}
                onClick={() => { navigator.clipboard?.writeText(text.trim()); toast.success("Copied"); }}>
                <Copy className="w-3.5 h-3.5 mr-1" />Copy
              </Button>
              <Button size="sm" variant="outline" disabled={!!busy} data-testid={`req-mark-texted-${tid}`}
                onClick={() => call("mark", () => axios.post(`${API}/mark-texted`, { key: it.key }), "Marked as texted")}>
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Mark as texted
              </Button>
              <Button size="sm" className="bg-gradient-to-r from-emerald-500 to-emerald-600" disabled={over || !text.trim() || !!busy || it.send_status === "sending"}
                onClick={() => setConfirm(true)} data-testid={`req-send-${tid}`}>
                <Send className="w-3.5 h-3.5 mr-1" />Send via Gingr
              </Button>
            </div>
          </div>
          {it.send_status === "failed" && it.send_error && <p className="text-xs text-rose-600" data-testid={`req-send-error-${tid}`}>{it.send_error}</p>}
        </div>
      ) : (
        <p className="mt-3 text-xs text-gray-400" data-testid={`req-no-draft-${tid}`}>No message template for {it.service || "this service"} — boarding requests only.</p>
      )}

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid={`req-confirm-${tid}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Text {it.owner.first_name} via Gingr?</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-wrap">{text.trim()}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`req-confirm-cancel-${tid}`}>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={`req-confirm-send-${tid}`}
              onClick={() => call("send", () => axios.post(`${API}/send`, { key: it.key, text: text.trim() }), "Sending via Gingr — about 30 seconds")}>
              Send now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default function RequestedTab() {
  const [data, setData] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try { setData((await axios.get(API)).data); } catch (e) { toast.error(e?.response?.data?.detail || "Could not load requests"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); timer.current = setInterval(load, 60000); return () => clearInterval(timer.current); }, [load]);
  useEffect(() => {
    if (!data.items.some((i) => i.send_status === "sending")) return undefined;
    const t = setTimeout(load, 5000);
    return () => clearTimeout(t);
  }, [data, load]);

  const refresh = async () => {
    setRefreshing(true);
    try { const r = (await axios.post(`${API}/refresh`)).data; toast.success(r.summary); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Refresh failed"); } finally { setRefreshing(false); }
  };

  return (
    <div className="space-y-4" data-testid="requested-tab">
      <div className="flex flex-wrap items-center gap-3">
        <OpsSectionHead icon={Inbox} title="Requested" sub={<>Gingr reservation requests waiting for confirmation. Checked every 15 min (7am–7pm) · last check <b data-testid="req-scanned-at">{ago(data.scanned_at)}</b></>} />
        <div className="ml-auto" />
        <RequestedMessageConfig onSaved={load} />
        <Button size="sm" variant="outline" disabled={refreshing} onClick={refresh} data-testid="req-refresh-btn">
          {refreshing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}Refresh now
        </Button>
      </div>
      {loading ? (
        <div className="py-12 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : data.items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 py-12 text-center text-gray-500" data-testid="req-empty">
          <Inbox className="w-8 h-8 mx-auto mb-2 text-gray-300" />No pending requests in Gingr — nothing to send.
        </div>
      ) : (
        <div className="space-y-3" data-testid="req-list">
          {data.items.map((it) => <RequestCard key={it.key} it={it} gingrUrl={data.gingr_app_url} onChanged={load} />)}
        </div>
      )}
    </div>
  );
}
